import { ReactNode, useEffect, useRef, useState } from 'react'

import { animate, useReducedMotion } from 'framer-motion'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { COUNT_UP_DURATION_S, COUNT_UP_EASE } from './motion'
import { PortraitMosaicQuote, PortraitMosaicQuoteBlock } from './PortraitMosaicQuoteBlock'
import { PortraitMosaicTone } from './PortraitMosaicStrip'

/**
 * KPI mode data (S3, Variant K — docs/plans/portrait-mosaic-strip-ui.md).
 * Shape is pinned by the plan and mirrors `MagazineBandKpi`
 * (`StudyDocMagazineBand/resolveMagazineBandSource.ts`) field-for-field, but
 * is declared locally on purpose: this component must not import from
 * `resolveMagazineBandSource` (derivation stays that module's job, this one
 * only renders). `count`/`total` are respondent counts deduped by
 * persona_id and must always drive the sub-line — never the number of tiles
 * actually rendered, which can be lower once the panel exceeds the tile cap
 * (`buildPortraitMosaicTiles.ts` samples down proportionally past the cap).
 */
export interface PortraitMosaicKpiData {
  pct: number
  label: string
  count: number
  total: number
  memberPersonaIds?: Array<string>
  membership: 'derived' | 'filled'
}

/**
 * KPI mode chrome (S3): headline + sub-line above the tile panel, one
 * supporting quote below it. The tile panel itself is NOT rendered here —
 * per D4 ("pass through the existing highlight mechanism"), the lit set for
 * KPI mode is `kpi.memberPersonaIds` fed into `PortraitMosaicStrip`'s
 * existing `highlightedPersonaIds`/`speakerPersonaId` props by the caller;
 * this component only wraps the grid the strip already renders (passed in
 * as `children`) with the KPI headline above and the quote below.
 *
 * Supporting quote: reuses S2's `PortraitMosaicQuoteBlock` + `PortraitMosaicQuote`
 * verbatim (single-element `quotes` array — with length <= 1 its rotation
 * timer never fires, so it renders as a static quote) rather than a
 * hand-rolled block, so KPI and quotes mode share one typography/formatting
 * contract (1.15rem app font, no glyphs, `quoteIdentityBlurb`-built
 * attribution) and one aria-live/crossfade implementation.
 */
interface PortraitMosaicKpiProps {
  kpi: PortraitMosaicKpiData
  tone?: PortraitMosaicTone
  /** Supporting quote for the ringed speaker. Omitted → headline + tiles only, no quote block. */
  quote?: PortraitMosaicQuote
  /** The already-rendered tile grid from `PortraitMosaicStrip`. */
  children: ReactNode
  className?: string
}

/**
 * Animates the displayed value from its previous value to `target` whenever
 * `target` changes (never on mount — first render shows `target` directly).
 * Jumps instantly under reduced motion. Framer-motion's imperative `animate`
 * drives a plain number via `onUpdate`, not a `motion.*` element, because
 * the value renders as text content inside a `Heading`, not a style prop.
 *
 * Duration/ease come from `./motion.ts` (`COUNT_UP_DURATION_S`/`COUNT_UP_EASE`) —
 * S4 consolidated this out of the local constants S3 shipped here.
 */
function useCountUpPct(target: number): number {
  const shouldReduceMotion = useReducedMotion()
  const [displayValue, setDisplayValue] = useState(target)
  const previousTargetRef = useRef(target)

  useEffect(() => {
    if (previousTargetRef.current === target) {
      return
    }
    if (shouldReduceMotion) {
      previousTargetRef.current = target
      setDisplayValue(target)
      return
    }
    const from = previousTargetRef.current
    previousTargetRef.current = target
    const controls = animate(from, target, {
      duration: COUNT_UP_DURATION_S,
      ease: COUNT_UP_EASE,
      onUpdate: (value) => setDisplayValue(Math.round(value)),
    })
    return () => controls.stop()
  }, [target, shouldReduceMotion])

  return displayValue
}

/**
 * "59 of 72 respondents · lit faces are the 82%" — sentence case, no
 * uppercase/tracking (the frozen UI spec overrides the throwaway preview's
 * kicker styling), middot separator, no em dashes. `count`/`total` are the
 * real respondent counts from the caller, never the rendered tile count.
 *
 * This is UI chrome, so it follows the i18n UI language, not the thread's
 * content language — same as the `choseLabel` headline directly above it.
 * The respondent noun pluralizes on the DENOMINATOR, so `kpi.total` travels
 * as i18next's `count` and the numerator travels as `selected`.
 */
function kpiSubline(
  t: TFunction<'app'>,
  kpi: PortraitMosaicKpiData,
  displayPct: number,
): string {
  return t('studyDocChrome.kpiSubline', {
    selected: kpi.count,
    count: kpi.total,
    pct: displayPct,
  })
}

function noopSetActiveIndex() {
  // KPI mode shows a single supporting quote — nothing rotates, so the
  // quote block's index setter has nothing to do.
}

export function PortraitMosaicKpi(props: PortraitMosaicKpiProps) {
  const { kpi, tone, quote, children, className } = props

  const { t } = useTranslation('app')
  const displayPct = useCountUpPct(kpi.pct)

  return (
    <div
      className={cn('flex flex-col gap-6', className)}
      data-testid="portrait-mosaic-kpi"
      data-membership={kpi.membership}
      data-tone={tone ?? 'dark'}
    >
      <div className="mx-auto max-w-[42rem] px-6" data-testid="portrait-mosaic-kpi-headline">
        <Heading
          level={2}
          weight="medium"
          className="font-sans font-medium not-italic normal-case tracking-normal leading-tight"
        >
          {/*
            The % is the hero through SIZE first (3.4rem vs the label's 2.4rem). In light
            mode it stays strong band ink: the reference's neon accent is a dark-mode-only
            treatment and was explicitly rejected for light mode (Fernando, 2026-09-03), and
            the brand accent at text size has no contrast guarantee on a light surface. Dark
            mode keeps the accent, which reads well on the deep slab.
          */}
          <span
            className="text-[3.4rem] font-medium not-italic leading-none text-foreground dark:text-[hsl(var(--brand-accent,var(--primary)))]"
            data-testid="portrait-mosaic-kpi-pct"
          >
            {displayPct}%
          </span>{' '}
          <span
            className="text-[2.4rem] font-medium not-italic leading-none"
            data-testid="portrait-mosaic-kpi-label"
          >
            {t('studyDocChrome.choseLabel', { label: kpi.label })}
          </span>
        </Heading>
        <Text
          as="p"
          size="sm"
          color="muted"
          weight="normal"
          className="mt-2 font-sans font-normal not-italic normal-case tracking-normal"
          data-testid="portrait-mosaic-kpi-subline"
        >
          {kpiSubline(t, kpi, displayPct)}
        </Text>
      </div>

      {children}

      {quote ? (
        <PortraitMosaicQuoteBlock
          quotes={[quote]}
          activeIndex={0}
          setActiveIndex={noopSetActiveIndex}
          className="mx-auto max-w-[42rem] px-6"
        />
      ) : null}
    </div>
  )
}
